import EventNames from '@/lib/Events/EventNames';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import TradingSessionEvent from '@/lib/TradingSessions/TradingSessionEvent';
import TradingSessionEventType from '@/lib/TradingSessions/TradingSessionEventType';
import { cloneDeep } from '@/functions/clone';
import { container } from '@/container';
import { createApp, h } from 'vue';
import { GlobalMethodsPlugin } from '@/vue/globalMethods';

/**
 * Returns an array of options to be used when creating the root Vue instance for a screen.
 *
 * @return {{created(): Promise<void>, methods: {updateViewPrivileges(): void}}}
 */
export const rootVueAppOptions = () => {
    return {
        /**
         * When the root Vue app is created set up some watchers
         * to listen for changes to things in Angular-land and mirror them in Vuex.
         */
        async created() {
            const $rootScope = container.$rootScope;

            // currentUser
            const updateStoreUser = () => {
                this.$store.commit('setCurrentUser', cloneDeep(container.userService.currentUser));

                this.updateViewPrivileges();
            };
            $rootScope.$on(EventNames.USER_LOGGED_IN, updateStoreUser);
            updateStoreUser();

            // currentBranch
            const updateStoreBranch = () => {
                this.$store.commit('setCurrentBranch', cloneDeep(container.userService.currentBranch));
            };
            $rootScope.$on(EventNames.ORDER_BRANCH_CHANGED, updateStoreBranch);
            updateStoreBranch();

            // currentItemBranch
            const updateStoreItemBranch = () => {
                this.$store.commit('setCurrentItemBranch', cloneDeep(container.userService.currentItemBranch));
            };
            $rootScope.$on(EventNames.ITEM_BRANCH_CHANGED, updateStoreItemBranch);
            updateStoreItemBranch();

            // currentDevice
            const updateStoreDevice = () => {
                this.$store.commit('setCurrentDevice', cloneDeep(container.currentDevice.device));
            };
            $rootScope.$on(EventNames.DEVICE_SET, updateStoreDevice);
            updateStoreDevice();

            // When changing companies, the privileges are not automatically refreshed when a new device is selected.
            $rootScope.$on(EventNames.TRADING_SESSION_CREATED, this.updateViewPrivileges);

            // isOnline
            container.connectivityChecker.addObserver(
                {
                    key: 'vue',
                    notifyOnline: () => {
                        if (this.$store.state.isOnline === false) {
                            container.tradingSessionManager.saveEvent(
                                new TradingSessionEvent(TradingSessionEventType.DEVICE_ONLINE)
                            ).catch(() => {});
                            container.toastFactory.success(
                                'ONLINE_STATUS.NOTIFICATION_TITLE__ONLINE',
                                container.langService.getText('ONLINE_STATUS.NOTIFICATION_MSG__ONLINE'),
                                {
                                    timeOut: 5000,
                                },
                            );
                        }

                        this.$store.commit('setIsOnline', true);
                    },
                    notifyOffline: () => {
                        if (this.$store.state.isOnline === true) {
                            container.tradingSessionManager.saveEvent(
                                new TradingSessionEvent(TradingSessionEventType.DEVICE_OFFLINE)
                            ).catch(() => {});
                            container.toastFactory.warning(
                                'ONLINE_STATUS.NOTIFICATION_TITLE__OFFLINE',
                                container.langService.getText('ONLINE_STATUS.NOTIFICATION_MSG__OFFLINE'),
                                {
                                    timeOut: 5000,
                                },
                            );
                        }

                        this.$store.commit('setIsOnline', false);
                    },
                },
            );

            // TODO: Fix this in PROP-587.
            // This is here so we have the status available at startup.
            // But it means we now have 3 status checks happening.
            await container.connectivityChecker.check();

            this.$store.commit('setIsOnline', container.connectivityChecker.isOnline());

            const updateCurrentPageHash = () => this.$store.commit('setCurrentPageHash', window.location.hash);
            window.addEventListener('hashchange', updateCurrentPageHash);
            // hashchange doesn't fire if you load the app with a hash already there so fire it once now.
            updateCurrentPageHash();

            this.$store.commit('setEposVersionCurrent', container.appConfig.getVersion());
            const checkForUpdates = async () => {
                if (this.$store.state.isOnline) {
                    try {
                        const url = 'https://' + window.location.host + '/version.json';
                        let remoteVersion = await fetch(url)
                            .then((response) => response.json());
                        this.$store.commit('setEposVersionLatest', remoteVersion);
                    } catch (error) {
                        console.log('Unable to check version', error);
                    }
                }
            };

            // Check for updates now.
            checkForUpdates()
                .catch(() => {});

            // Check for updates on an interval.
            setInterval(checkForUpdates, container.appConfig.updateCheckFrequency);

            // Check for updates when the app resumes after losing focus (being minimized or switching tab).
            // Run a connectivity check when the app resumes after losing focus (being minimized or switching tab).
            window.addEventListener(
                'focus',
                checkForUpdates,
                {
                    passive: true,
                }
            );

            this.$store.commit('setIsDev', container.appConfig.isDev);
            this.$store.commit('setIsStaging', container.appConfig.environment === 'Staging');
        },

        methods: {
            updateViewPrivileges() {
                let privilegeMappings = {
                    [PrivilegeNames.ACCESS_ACCOUNTS]: 'accounts',
                    [PrivilegeNames.ACCESS_REDEEM]: 'redeem',
                    [PrivilegeNames.ACCESS_REPORT]: 'reports',
                    [PrivilegeNames.ACCESS_SELL]: 'sell',
                    [PrivilegeNames.ACCESS_TODAY]: 'today',
                };

                container.privilegesManager
                    .checkMultipleCurrentUserPrivileges(Object.keys(privilegeMappings))
                    .then(
                        /**
                         * @param {PrivilegeCheckResult[]} results
                         */
                        (results) => {
                            Object.values(results).forEach((result) => {
                                const viewName = privilegeMappings[result.privilegeName];
                                this.$store.commit('setViewPrivilege', {
                                    viewName,
                                    value: result.granted,
                                });
                            });
                        },
                    );
            },
        },
    };
};

/**
 * Create (but don't mount) the root Vue app.
 *
 * @param rootComponent The component to use as the first child of the root component.
 * @param {Store<{}>} vuexStore A Vuex store instance to use.
 * @param options {{}} Additional options to add to the root App.
 * @param rootProps {{}} Props to pass to the root component.
 *
 * @return {App<Element>}
 */
export const createVueApp = (rootComponent, vuexStore, options = {}, rootProps = {}) => {
    const app = createApp(
        {
            render: () => h(rootComponent),
            ...options,
        },
        rootProps
    );

    app.use(vuexStore);
    app.use(GlobalMethodsPlugin);

    return app;
};
